import type { PaginatedResponse } from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';
import { extractServerError } from '@/lib/api-error';
import { runInChunks } from '@/lib/bulk-in-chunks';

/**
 * Lead Parsing System, as the CRM exposes it (apps/api `lps-admin` module).
 * Inbox reads come from the lps_* tables; connection calls are relayed to LPS.
 *
 * Passthrough types rather than shared Zod schemas: the connection config is
 * connector-defined JSON validated by LPS itself, and the lead shapes are
 * still moving while the remaining providers are tested live.
 */

export type LpsSource = 'WEBSITE' | 'META_LEAD_ADS' | 'WHATSAPP' | 'LANDING_PAGE' | 'MANUAL';
export type LpsLeadStatus = 'NEW' | 'DUPLICATE' | 'INCOMPLETE';
/**
 * PARKED = failed and not retrying on its own (CRM rejected it or attempts ran out).
 * AWAITING_ASSIGNMENT = round-robin found no expert; waits for a person to pick one.
 */
export type LpsForwardState =
  | 'PENDING'
  | 'FORWARDED'
  | 'FAILED'
  | 'PARKED'
  | 'SKIPPED'
  | 'AWAITING_ASSIGNMENT';

export interface LpsLeadItem {
  id: string;
  receivedAt: string;
  occurredAt: string;
  source: LpsSource;
  connectorId: string;
  connection: { id: string; name: string; deleted: boolean };
  fullName: string | null;
  email: string | null;
  phone: string | null;
  destination: string | null;
  message: string | null;
  status: LpsLeadStatus;
  duplicateOfId: string | null;
  forward: {
    status: LpsForwardState;
    attempts: number;
    error: string | null;
    nextAttemptAt: string | null;
    forwardedAt: string | null;
    crmLeadId: string | null;
    crmLeadReference: string | null;
  };
  assignment: {
    agentId: string | null;
    agentName: string | null;
    /** When the agent took it; null while nobody holds the lead. */
    assignedAt: string | null;
    method: 'ROUND_ROBIN' | 'MANUAL' | null;
    /** Agent picked in the queue for the next send. */
    requestedUserId: string | null;
  };
  campaign: string | null;
  /** Paid vs organic, as the CRM classifies it. */
  channel: { channel: LpsMarketingChannel; isPaid: boolean; platform: string | null };
}

export type LpsMarketingChannel =
  | 'PAID_SOCIAL'
  | 'PAID_SEARCH'
  | 'PAID_OTHER'
  | 'ORGANIC_SOCIAL'
  | 'ORGANIC_SEARCH'
  | 'REFERRAL'
  | 'DIRECT'
  | 'EMAIL'
  | 'WHATSAPP'
  | 'OFFLINE';

export interface LpsAgentOption {
  id: string;
  fullName: string;
  role: string;
  destinations: string[];
  /** Covers the destination this lead resolved to. */
  isExpert: boolean;
}

export interface LpsLeadAgents {
  destination: { id: string; name: string } | null;
  agents: LpsAgentOption[];
}

export type LpsDeliveryOutcome =
  | 'ACCEPTED'
  | 'DUPLICATE_ONLY'
  | 'NO_LEAD'
  | 'HANDSHAKE'
  | 'REJECTED_SECRET'
  | 'REJECTED_SIGNATURE'
  | 'CONNECTION_DISABLED'
  | 'CONNECTOR_MISSING'
  | 'INVALID_JSON'
  | 'ERROR';

export interface LpsDeliveryItem {
  id: string;
  receivedAt: string;
  connectionId: string;
  connectionName: string | null;
  connectorId: string | null;
  method: 'GET' | 'POST';
  outcome: LpsDeliveryOutcome;
  httpStatus: number;
  accepted: number;
  duplicates: number;
  skipped: number;
  leadIds: string[];
  notes: string[];
  error: string | null;
  bodyBytes: number;
  durationMs: number | null;
}

export interface LpsDeliveryDetail extends LpsDeliveryItem {
  body: unknown;
  bodyText: string | null;
  query: Record<string, unknown> | null;
  headers: Record<string, unknown> | null;
}

export interface LpsDeliveryListQuery {
  page: number;
  pageSize: number;
  sortDir?: 'asc' | 'desc';
  connectionId?: string;
  outcome?: LpsDeliveryOutcome | 'PROBLEMS';
}

export interface LpsLeadDetail extends LpsLeadItem {
  /** The webhook request this lead came from, when still in the log. */
  delivery: { id: string; receivedAt: string; outcome: LpsDeliveryOutcome } | null;
  phoneRaw: string | null;
  externalId: string | null;
  trip: {
    destination: string | null;
    travelDate: string | null;
    nights: number | null;
    adults: number | null;
    children: number | null;
    budget: string | null;
  };
  attribution: Record<string, string | null>;
  /** Admin-only; null with `rawHidden` true for everyone else. */
  raw: unknown;
  rawHidden: boolean;
}

export interface LpsOverview {
  total: number;
  duplicates: number;
  forwarded: number;
  pending: number;
  awaitingAssignment: number;
  retrying: number;
  parked: number;
  skipped: number;
}

export type LpsLeadType = 'organic' | 'inorganic';

export interface LpsLeadListQuery {
  page: number;
  pageSize: number;
  sortBy?: 'receivedAt' | 'occurredAt';
  sortDir?: 'asc' | 'desc';
  search?: string;
  source?: LpsSource;
  status?: LpsLeadStatus;
  forwardStatus?: LpsForwardState;
  connectionId?: string;
  /** Exact destination name, or 'none' for leads without one. */
  destination?: string;
  /** A user id, or 'none' for leads nobody has taken. */
  agentId?: string;
  /** Inorganic = from an ad; organic = everything else. Omit for all. */
  leadType?: LpsLeadType;
  from?: string;
  to?: string;
  /** Assigned to an agent within these IST days (YYYY-MM-DD). */
  assignedFrom?: string;
  assignedTo?: string;
}

export interface LpsDestinationSummary {
  from: string | null;
  to: string | null;
  /** Leads received in the window. */
  total: number;
  /** Leads handed to an agent in the window, whenever they arrived ("cumulative"). */
  assignedTotal: number;
  inCrm: number;
  waiting: number;
  /** Columns, busiest first; `destination: null` is the "not worked out" one. */
  destinations: Array<{ destination: string | null; total: number; assignedTotal: number }>;
  /** Rows; `agentName: null` is the leads nobody holds yet. */
  agents: Array<{
    agentId: string | null;
    agentName: string | null;
    total: number;
    assignedTotal: number;
    /** Received in the window, keyed by destination name, with '' for no destination. */
    counts: Record<string, number>;
    /** Assigned in the window, same keys. Always 0 for the unassigned row. */
    assignedCounts: Record<string, number>;
  }>;
}

/** A count with its paid (inorganic) / organic split; total = paid + organic. */
export interface LpsLeadSplit {
  total: number;
  paid: number;
  organic: number;
}

/** Which platform sends leads for which destination, in a received-date window. */
export interface LpsChannelSummary extends LpsLeadSplit {
  from: string | null;
  to: string | null;
  /** Columns, busiest first; `platform: null` is "other / unknown", last. */
  platforms: Array<LpsLeadSplit & { platform: string | null }>;
  /** Rows; `destination: null` is the "not worked out" one, last. */
  destinations: Array<
    LpsLeadSplit & {
      destination: string | null;
      /** Keyed by platform, with '' for other / unknown. */
      counts: Record<string, LpsLeadSplit>;
    }
  >;
}

export interface LpsSummaryQuery {
  from?: string;
  to?: string;
  leadType?: LpsLeadType;
}

/** The subset of JSON Schema that zod's toJSONSchema emits for connector configs. */
export interface JsonSchemaNode {
  type?: string;
  enum?: ReadonlyArray<string>;
  default?: unknown;
  description?: string;
  items?: JsonSchemaNode;
  properties?: Record<string, JsonSchemaNode>;
  required?: ReadonlyArray<string>;
  additionalProperties?: boolean | JsonSchemaNode;
  propertyNames?: JsonSchemaNode;
  minimum?: number;
  maximum?: number;
  minLength?: number;
}

export interface LpsConnector {
  id: string;
  label: string;
  source: LpsSource;
  description: string;
  supportsChallenge: boolean;
  supportsVerification: boolean;
  secretFields: ReadonlyArray<string>;
  configSchema: JsonSchemaNode;
}

export interface LpsConnection {
  id: string;
  connectorId: string;
  name: string;
  enabled: boolean;
  /** Secret fields arrive masked ("****abcd"). */
  config: Record<string, unknown>;
  secret: string;
  createdAt: string;
  updatedAt: string;
  lastEventAt: string | null;
  leadCount: number;
  crmSourceCode: string | null;
  forwardToCrm: boolean;
  webhookUrl: string;
}

export interface LpsConnectionInput {
  connectorId?: string;
  name?: string;
  enabled?: boolean;
  config?: Record<string, unknown>;
  crmSourceCode?: string | null;
  forwardToCrm?: boolean;
}

export const listLpsLeads = async (
  query: LpsLeadListQuery,
): Promise<PaginatedResponse<LpsLeadItem>> => {
  const { data } = await apiClient.get<PaginatedResponse<LpsLeadItem>>('/lps/leads', {
    params: query,
  });
  return data;
};

export const getLpsLead = async (id: string): Promise<LpsLeadDetail> => {
  const { data } = await apiClient.get<LpsLeadDetail>(`/lps/leads/${id}`);
  return data;
};

/**
 * The destination / channel summaries are also embedded on the Marketing Report,
 * which serves the SAME data under its own permission at a different path. Callers
 * pass that path to read it there instead of the LPS endpoint; it defaults to LPS.
 */
export const getLpsDestinationSummary = async (
  query: LpsSummaryQuery,
  path = '/lps/leads/by-destination',
): Promise<LpsDestinationSummary> => {
  const { data } = await apiClient.get<LpsDestinationSummary>(path, { params: query });
  return data;
};

export const getLpsChannelSummary = async (
  query: LpsSummaryQuery,
  path = '/lps/leads/by-channel',
): Promise<LpsChannelSummary> => {
  const { data } = await apiClient.get<LpsChannelSummary>(path, { params: query });
  return data;
};

export const getLpsOverview = async (): Promise<LpsOverview> => {
  const { data } = await apiClient.get<LpsOverview>('/lps/overview');
  return data;
};

export const retryLpsLead = async (id: string): Promise<void> => {
  await apiClient.post(`/lps/leads/${id}/forward`);
};

export const getLpsLeadAgents = async (id: string): Promise<LpsLeadAgents> => {
  const { data } = await apiClient.get<LpsLeadAgents>(`/lps/leads/${id}/agents`);
  return data;
};

/** userId null = let round-robin try again. */
export const assignLpsLead = async (
  id: string,
  userId: string | null,
): Promise<LpsLeadItem['forward'] | null> => {
  const { data } = await apiClient.post<{ forward?: LpsLeadItem['forward'] } | null>(
    `/lps/leads/${id}/assign`,
    {
      userId,
    },
  );
  return data?.forward ?? null;
};

export interface LpsBulkAssignResult {
  /** Created in the CRM under the agent. */
  assigned: number;
  /** Sent, but still waiting for an agent (round-robin found nobody). */
  waiting: number;
  failed: Array<{ id: string; error: string }>;
}

/**
 * Same as `assignLpsLead` for a selection; userId null = round-robin each one.
 * Sent in small chunks (see runInChunks) and folded back into one result.
 */
export const bulkAssignLpsLeads = async (
  ids: ReadonlyArray<string>,
  userId: string | null,
  onProgress?: (done: number, total: number) => void,
): Promise<LpsBulkAssignResult> => {
  const run = await runInChunks(
    ids,
    async (chunk) => {
      const { data } = await apiClient.post<LpsBulkAssignResult>('/lps/leads/bulk-assign', {
        ids: chunk,
        userId,
      });
      return data;
    },
    {
      ...(onProgress ? { onProgress } : {}),
      errorText: (err) => extractServerError(err).message ?? 'Request failed',
    },
  );
  return {
    assigned: run.results.reduce((n, r) => n + r.assigned, 0),
    waiting: run.results.reduce((n, r) => n + r.waiting, 0),
    failed: [...run.results.flatMap((r) => r.failed), ...run.unsent],
  };
};

export const listLpsDeliveries = async (
  query: LpsDeliveryListQuery,
): Promise<PaginatedResponse<LpsDeliveryItem>> => {
  const { data } = await apiClient.get<PaginatedResponse<LpsDeliveryItem>>('/lps/deliveries', {
    params: query,
  });
  return data;
};

export const getLpsDelivery = async (id: string): Promise<LpsDeliveryDetail> => {
  const { data } = await apiClient.get<LpsDeliveryDetail>(`/lps/deliveries/${id}`);
  return data;
};

export const listLpsConnectors = async (): Promise<LpsConnector[]> => {
  const { data } = await apiClient.get<{ items: LpsConnector[] }>('/lps/connectors');
  return data.items;
};

export const listLpsConnections = async (): Promise<LpsConnection[]> => {
  const { data } = await apiClient.get<{ items: LpsConnection[] }>('/lps/connections');
  return data.items;
};

export const createLpsConnection = async (input: LpsConnectionInput): Promise<LpsConnection> => {
  const { data } = await apiClient.post<LpsConnection>('/lps/connections', input);
  return data;
};

export const updateLpsConnection = async (
  id: string,
  input: LpsConnectionInput,
): Promise<LpsConnection> => {
  const { data } = await apiClient.patch<LpsConnection>(`/lps/connections/${id}`, input);
  return data;
};

export const rotateLpsConnectionSecret = async (id: string): Promise<LpsConnection> => {
  const { data } = await apiClient.post<LpsConnection>(`/lps/connections/${id}/rotate-secret`);
  return data;
};

export const deleteLpsConnection = async (id: string): Promise<void> => {
  await apiClient.delete(`/lps/connections/${id}`);
};
